import noop from 'lodash/noop';
import { v4 } from 'uuid';

import {
  LogLevel,
  LogMetadata,
  Loggable,
  isDebugEnabled,
} from './loggers/base';
import { CliConsoleLogger } from './loggers/cli-console';
import { ConsolaLogger } from './loggers/consola-logger';
import { ConsoleLogger } from './loggers/console';
import { DatadogBrowserLogger } from './loggers/datadog-browser-logger';
import { DriverBasedLogger } from './loggers/driver-based-logger';
import { LocalLokiLogger } from './loggers/local-loki-logger';

export type { LogLevel, LogMetadata, Loggable };

export { errorGroupBeforeSend } from './datadog-logs-before-send';

export {
  ConsolaLogger,
  DriverBasedLogger,
  ConsoleLogger,
  DatadogBrowserLogger,
  CliConsoleLogger,
  LocalLokiLogger,
  isDebugEnabled,
};

const MAX_CAUSE_DEPTH = 5;

type SerializedError = {
  name?: string;
  message?: string;
  stack?: string;
};

const serializeError = (error: unknown): SerializedError => {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }
  if (error && typeof error === 'object') {
    const obj = error as { name?: unknown; message?: unknown };
    return {
      name: typeof obj.name === 'string' ? obj.name : undefined,
      message: typeof obj.message === 'string' ? obj.message : undefined,
    };
  }
  return { message: String(error) };
};

/**
 * Best-effort human-readable message for any thrown value.
 *
 * Unlike `String(error)`, this reads the `message` field off `Error`
 * instances AND plain `{ message }` objects (e.g. `chrome.runtime.lastError`),
 * which `String()` would coerce to the useless `"[object Object]"`. Use this
 * when matching error text against known-noise substrings.
 */
export const getErrorMessage = (error: unknown): string =>
  error == null ? '' : (serializeError(error).message ?? '');

const getNextCause = (error: unknown): unknown => {
  if (!error || typeof error !== 'object') return undefined;
  // `cause` is the standard Error API; `inner` is Dexie's transaction-abort
  // convention. Modern Dexie sets both to the same value, so `cause ?? inner`
  // covers either source; we don't branch into both legs.
  const obj = error as { cause?: unknown; inner?: unknown };
  return obj.cause ?? obj.inner;
};

const isDOMException = (value: unknown): value is DOMException =>
  typeof DOMException !== 'undefined' && value instanceof DOMException;

const collectCauseChain = (error: unknown): SerializedError[] => {
  const chain: SerializedError[] = [];
  const seen = new Set<unknown>([error]);
  let current = getNextCause(error);

  while (
    current != null &&
    chain.length < MAX_CAUSE_DEPTH &&
    !seen.has(current)
  ) {
    seen.add(current);
    chain.push(serializeError(current));
    current = getNextCause(current);
  }
  return chain;
};

const QUOTA_EXCEEDED_ERROR_NAME = 'QuotaExceededError';

/**
 * True if `error` or any link in its cause/inner chain is a QuotaExceededError.
 * Dexie wraps the quota DOMException as an `AbortError` on `.inner`, so a
 * top-level name check misses it — walk the same chain `collectCauseChain` does.
 */
export const isQuotaExceededError = (error: unknown): boolean => {
  const seen = new Set<unknown>();
  let current: unknown = error;

  while (
    current != null &&
    !seen.has(current) &&
    seen.size <= MAX_CAUSE_DEPTH
  ) {
    seen.add(current);
    if (serializeError(current).name === QUOTA_EXCEEDED_ERROR_NAME) {
      return true;
    }
    current = getNextCause(current);
  }
  return false;
};

type Options = {
  logger?: Loggable;
  metadata?: LogMetadata;
  consoleLogWarningsAsInfo?: boolean;
};

export class Logger {
  public logger: Loggable;
  private internalMetadata: LogMetadata;
  private consoleLogWarningsAsInfo: boolean;

  /**
   * @deprecated Don't instantiate this class directly unless you have a specific use case.
   * Prefer using `useNamedLogger` or `useExtendedLogger` hooks which will provide you with a logger instance
   * and will automatically include important metadata in the logs eg `tenantId`, `userId`, `appointmentId` etc.
   */
  constructor(prefix = 'eluve-logger', options: Options = {}) {
    const { logger, metadata, consoleLogWarningsAsInfo } = options;
    this.consoleLogWarningsAsInfo = Boolean(consoleLogWarningsAsInfo);
    this.internalMetadata = metadata || {};

    if (logger) {
      this.logger = logger;
    } else {
      this.logger = new DatadogBrowserLogger(prefix, metadata);
    }
  }

  private extractErrorDetails(error: Error | DOMException) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      causeChain: collectCauseChain(error),
      error,
    };
  }

  private normalizeMetadata(metadata?: LogMetadata): LogMetadata | undefined {
    if (!metadata) {
      return this.internalMetadata;
    }

    if (metadata instanceof Error || isDOMException(metadata)) {
      return {
        ...this.internalMetadata,
        error: metadata,
        errorDetails: this.extractErrorDetails(metadata),
      };
    }

    const error = metadata['error'];
    if (error instanceof Error || isDOMException(error)) {
      return {
        ...this.internalMetadata,
        ...metadata,
        errorDetails: this.extractErrorDetails(error),
      };
    }

    return { ...this.internalMetadata, ...metadata };
  }

  info = (message: string, metadata?: LogMetadata) => {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    this.logger.info(message, normalizedMetadata);
  };

  warn = (message: string, metadata?: LogMetadata) => {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    if (this.consoleLogWarningsAsInfo) {
      this.logger.info(message, normalizedMetadata);
    } else {
      this.logger.warn(message, normalizedMetadata);
    }
  };

  error = (message: string, metadata?: LogMetadata) => {
    const normalizedMetadata = this.normalizeMetadata(metadata);
    this.logger.error(message, normalizedMetadata);
  };

  debug = (message: string, metadata?: LogMetadata) => {
    if (isDebugEnabled()) {
      const normalizedMetadata = this.normalizeMetadata(metadata);
      this.logger.debug(message, normalizedMetadata);
    }
  };

  get metadata() {
    return this.internalMetadata;
  }
}

export class NoopLogger extends Logger {
  constructor(prefix = 'eluve-logger') {
    super(prefix, {
      logger: {
        debug: noop,
        error: noop,
        info: noop,
        warn: noop,
      },
    });
  }
}

export type CorrelatedLogger = (
  level: LogLevel,
  message: string,
  metadata?: LogMetadata,
) => void;

export const createCorrelatedLogger = (
  logger: Logger,
  sharedContext: Record<string, string | number | boolean>,
  correlationId = v4(),
) => {
  const log: CorrelatedLogger = (level, message, metadata = {}) => {
    const payload = {
      correlationId,
      ...sharedContext,
    };

    logger[level](message, { ...payload, ...metadata });
  };

  return { correlationId, log };
};
